import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ApiService } from 'src/app/core/http/api.service';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ToolsValidations } from 'src/app/core/tools/tools.validators';
import { ApiCore } from 'src/app/config/api';
import { AuthService } from 'src/app/core/auth/auth.service';
import { SessionService } from 'src/app/core/auth/session.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'src/app/shared/services/toast.service';
import { IError, IResetPassword } from 'src/app/models/Auth/AuthInterfaces';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { SharedModule } from 'src/app/shared/modules/primeng.module';

@Component({
  selector: 'app-recover-password-modal',
  templateUrl: './recover-password-modal.component.html',
  styleUrls: ['./recover-password-modal.component.css'],
  standalone: true,
  imports: [SharedModule]
})
export class RecoverPasswordModalComponent implements OnInit {
  @Input('showModal') showEmailModal: boolean = false;
  @Output() hideModal = new EventEmitter<void>();
  logo: string = 'assets/images/no-photo-available.png';
  recoverPasswordForm: FormGroup;
  processing: boolean = false;

  showCodeModal: boolean = false;
  codeForm: FormGroup;

  showPasswordModal: boolean = false;
  passwordForm: FormGroup;

  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  recoveryEmail: string = '';

  constructor(
    private api: ApiService,
    private fb: FormBuilder,
    private authService: AuthService,
    private session: SessionService,
    private renderer: Renderer2,
    private toast: ToastService,
    private LoginModalComponent: LoginModalComponent
  ) {
    this.recoverPasswordForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(ToolsValidations.recoveryEmail),
        ],
      ],
    });

    this.codeForm = this.fb.group({
      digit1: [],
      digit2: [],
      digit3: [],
      digit4: [],
    });

    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, this.newPasswordValidator()]],
      confirmPassword: [
        '',
        [Validators.required, this.confirmPasswordValidator()],
      ],
    });
  }

  ngOnInit(): void {
    this.getCasinoConfig();
  }

  private getCasinoConfig(): void {
    this.api.getCoorporateCasinoConfig().subscribe({
      next: (res) => {
        if (res.data.logoCasinoUrl) {
          this.logo = res.data.logoCasinoUrl;
        }
      },
      error: (err) => console.error(err),
    });
  }

  onHide() {
    this.processing = false;
    this.hideModal.emit();
    this.recoverPasswordForm.reset();
    this.codeForm.reset();
    this.passwordForm.reset();
  }

  onSubmit() {
    this.processing = true;
    if (this.recoverPasswordForm.valid) {
      const email = this.recoverPasswordForm.get('email')?.value.toLowerCase();
      this.recoveryEmail = email;
      this.authService
        .recoverPassword({ email }, ApiCore.RECOVER_PASSWORD)
        .subscribe(
          (resp: any) => {
            this.processing = false;

            if (resp.statusCode !== 200) {
              return;
            }
            if (!this.session.getItem('recoveryEmail')) {
              this.session.setItem('recoveryEmail', email);
            }
            this.authService.recoverPasswordEmail = email;

            this.showEmailModal = false;
            this.showCodeModal = true;
          },
          (error: HttpErrorResponse) => {
            this.processing = false;
            console.log(error);
          }
        );
    } else {
      this.processing = false;
      this.recoverPasswordForm.markAllAsTouched();
    }
  }

  resendEmail() {
    this.processing = true;
    const email = this.recoveryEmail;
    this.authService
      .recoverPassword({ email }, ApiCore.RECOVER_PASSWORD)
      .subscribe(
        (resp: any) => {
          this.processing = false;

          if (resp.statusCode !== 200) {
            return;
          }
          if (!this.session.getItem('recoveryEmail')) {
            this.session.setItem('recoveryEmail', email);
          }
          this.authService.recoverPasswordEmail = email;

          this.showEmailModal = false;
          this.showCodeModal = true;
        },
        (error: HttpErrorResponse) => {
          this.processing = false;
          console.log(error);
        }
      );
  }

  @ViewChild('digit1') digit1!: ElementRef;
  @ViewChild('digit2') digit2!: ElementRef;
  @ViewChild('digit3') digit3!: ElementRef;
  @ViewChild('digit4') digit4!: ElementRef;
  onEnterCode(digit: number, event: any) {
    if (isNaN(event.data) || event.data === ' ' || event.data === null) {
      this.codeForm.get(`digit${digit}`)?.setValue(null);
      return;
    }

    switch (digit) {
      case 1:
        this.renderer.selectRootElement(this.digit2.nativeElement).focus();
        break;
      case 2:
        this.renderer.selectRootElement(this.digit3.nativeElement).focus();
        break;
      case 3:
        this.renderer.selectRootElement(this.digit4.nativeElement).focus();
        break;
      case 4:
        this.renderer.selectRootElement(this.digit4.nativeElement).blur();
        break;
    }
  }

  validateCurrentCode() {
    this.processing = true;

    const digit1 = this.codeForm.get(`digit1`)?.value;
    const digit2 = this.codeForm.get(`digit2`)?.value;
    const digit3 = this.codeForm.get(`digit3`)?.value;
    const digit4 = this.codeForm.get(`digit4`)?.value;

    const restoreCode = `${digit1}${digit2}${digit3}${digit4}`;

    this.api
      .create('auth/validate-restore-code', {
        restoreCode,
      })
      .subscribe(
        (res) => {
          this.authService.recoverCode = restoreCode;
          this.processing = false;
          this.showCodeModal = false;
          this.showPasswordModal = true;
        },
        (error) => {
          this.processing = false;
          this.toast.showError('Código inválido, favor de verificar', '');
        }
      );
  }

  private newPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      if (!value) return null;

      if (value.length < 8) {
        return { custom: `Utiliza al menos 8 caracteres` };
      }

      if (!/[0-9]/.test(value)) {
        return { custom: `Utiliza al menos 1 número` };
      }

      if (!/[A-Z]/.test(value)) {
        return { custom: `Utiliza al menos una letra mayúscula` };
      }

      if (!/[a-z]/.test(value)) {
        return { custom: `Utiliza al menos una letra minúscula` };
      }

      if (!/[!¡°'¬≠«≤≥…–^@#$%^&*(),.¿?":{}|<>[\]\/;:_+`~=\\-]/.test(value)) {
        return { custom: `Utiliza al menos 1 caracter especial` };
      }

      if (/\s/.test(value)) {
        return { custom: ` ` };
      }

      this.passwordForm.get('confirmPassword')?.updateValueAndValidity();

      return null;
    };
  }

  private confirmPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (!value) return null;

      if (value !== this.passwordForm.get('newPassword')?.value) {
        return { custom: `Contraseña no es igual` };
      }

      return null;
    };
  }

  changePassword() {
    const resetPassword = {
      restoreCode: this.authService.recoverCode,
      password: this.passwordForm.get('newPassword')?.value,
    };

    if (!resetPassword.restoreCode || !resetPassword.password) {
      this.toast.showError('Código inválido, favor de verificar', '');
      return;
    }

    this.processing = true;

    this.authService
      .recoverPassword(resetPassword, ApiCore.RESET_PASSWORD)
      .subscribe(
        async (resp: IResetPassword) => {
          this.processing = false;
          if (resp.statusCode === 403) {
            this.toast.showError('Código inválido, favor de verificar', '');
            return;
          }
          this.toast.showSuccess(
            'Contraseña actualizada satisfactoriamente',
            ''
          );

          this.session.removeItem('recoveryEmail');
          this.showPasswordModal = false;
          this.LoginModalComponent.emitHideModal();
          this.LoginModalComponent.showModal = true;
          this.LoginModalComponent.loginForm
            .get('user')
            ?.setValue(resp.userName);
          this.LoginModalComponent.loginForm.get('password')?.setValue('');
        },
        (error: HttpErrorResponse) => {
          this.processing = false;
          const RecoverError: IError = error.error;
          console.error(RecoverError);
          if (RecoverError.statusCode === 400) {
            this.toast.showError('Código inválido, favor de verificar', '');
            return;
          }
          this.toast.showError('Código inválido, favor de verificar', '');
        }
      );
  }
}
